/* ----------------------------------------------------------------------------
 * Content Studio v0 — static, BookedCentral-OWNED design catalogue + label maps.
 *
 * These are ORIGINAL BC layout compositions expressed in our own tokens (NO
 * Canva templates/assets copied). Layouts are a static UI catalogue, not
 * workspace data, so they ship as a constant — the browser makes NO network
 * call for them (the locked contract's 'server-defined constant, no table').
 * Growth here is content, not code (small on purpose).
 * -------------------------------------------------------------------------- */
import type {
  AspectKey,
  Channel,
  ContentType,
  LayoutSpec,
  PublishState,
  ReviewStatus,
} from '@/types/social';

/** Channels selectable in the studio + display names. */
export const CHANNELS: Array<{ key: Channel; label: string }> = [
  { key: 'instagram', label: 'Instagram' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'x', label: 'X' },
  { key: 'generic', label: 'Generic' },
];

export function channelLabel(channel: Channel): string {
  return CHANNELS.find((c) => c.key === channel)?.label ?? channel;
}

/**
 * Which orientations each channel offers + the concrete ratio used to frame the
 * preview. Instagram story is portrait 9:16; the standard IG feed is 4:5/1:1.
 */
export const CHANNEL_ASPECTS: Record<Channel, Array<{ key: AspectKey; label: string; ratio: string; css: number }>> = {
  instagram: [
    { key: 'portrait', label: 'Portrait 4:5', ratio: '4:5', css: 4 / 5 },
    { key: 'square', label: 'Square 1:1', ratio: '1:1', css: 1 },
    { key: 'landscape', label: 'Landscape 1.91:1', ratio: '1.91:1', css: 1.91 },
  ],
  facebook: [
    { key: 'landscape', label: 'Landscape 1.91:1', ratio: '1.91:1', css: 1.91 },
    { key: 'square', label: 'Square 1:1', ratio: '1:1', css: 1 },
    { key: 'portrait', label: 'Portrait 4:5', ratio: '4:5', css: 4 / 5 },
  ],
  linkedin: [
    { key: 'landscape', label: 'Landscape 1.91:1', ratio: '1.91:1', css: 1.91 },
    { key: 'square', label: 'Square 1:1', ratio: '1:1', css: 1 },
    { key: 'portrait', label: 'Portrait 4:5', ratio: '4:5', css: 4 / 5 },
  ],
  x: [
    { key: 'landscape', label: 'Landscape 16:9', ratio: '16:9', css: 16 / 9 },
    { key: 'square', label: 'Square 1:1', ratio: '1:1', css: 1 },
  ],
  generic: [
    { key: 'square', label: 'Square 1:1', ratio: '1:1', css: 1 },
    { key: 'landscape', label: 'Landscape 16:9', ratio: '16:9', css: 16 / 9 },
    { key: 'portrait', label: 'Portrait 4:5', ratio: '4:5', css: 4 / 5 },
  ],
};

export function aspectCss(channel: Channel, aspect: AspectKey): number {
  const found = CHANNEL_ASPECTS[channel]?.find((a) => a.key === aspect);
  return found?.css ?? 1;
}
export function defaultAspect(channel: Channel): AspectKey {
  return CHANNEL_ASPECTS[channel]?.[0]?.key ?? 'square';
}

/** The 7 curated BC-owned layouts (contract §5.2). */
export const CONTENT_LAYOUTS: LayoutSpec[] = [
  {
    layoutKey: 'hero-media',
    name: 'Hero media',
    description: 'Full-bleed photo or video with a headline and CTA overlaid at the base.',
    slots: ['media', 'headline', 'cta', 'logo'],
    supportedAspects: ['landscape', 'portrait', 'square'],
    carouselRole: 'any',
  },
  {
    layoutKey: 'split-media-text',
    name: 'Split media + text',
    description: 'Media on one half, headline and body copy on the other.',
    slots: ['media', 'headline', 'body', 'cta', 'logo'],
    supportedAspects: ['landscape', 'square'],
    carouselRole: 'any',
  },
  {
    layoutKey: 'text-card',
    name: 'Text card',
    description: 'Typographic card on a brand colour — no media required.',
    slots: ['headline', 'body', 'cta', 'logo', 'contact'],
    supportedAspects: ['square', 'portrait'],
    carouselRole: 'any',
  },
  {
    layoutKey: 'quote-card',
    name: 'Quote card',
    description: 'A single pulled quote with attribution, centred.',
    slots: ['headline', 'contact', 'logo'],
    supportedAspects: ['square', 'portrait'],
    carouselRole: 'any',
  },
  {
    layoutKey: 'carousel-cover',
    name: 'Carousel cover',
    description: 'Opening slide: bold headline + swipe cue.',
    slots: ['media', 'headline', 'logo'],
    supportedAspects: ['portrait', 'square'],
    carouselRole: 'cover',
  },
  {
    layoutKey: 'carousel-tip',
    name: 'Carousel tip',
    description: 'Numbered body slide for a step or tip.',
    slots: ['headline', 'body', 'media'],
    supportedAspects: ['portrait', 'square'],
    carouselRole: 'body',
  },
  {
    layoutKey: 'carousel-cta',
    name: 'Carousel CTA',
    description: 'Closing slide with the call to action and contact line.',
    slots: ['headline', 'cta', 'contact', 'logo'],
    supportedAspects: ['portrait', 'square'],
    carouselRole: 'cta',
  },
];

export function layoutByKey(key: string): LayoutSpec | undefined {
  return CONTENT_LAYOUTS.find((l) => l.layoutKey === key);
}

/**
 * SAFE font set (v0, licence-clean self-hostable/system — contract §7, gate
 * G-CSV-4). NO font upload anywhere. The brand-kit PUT rejects anything outside
 * this list server-side.
 */
export const SAFE_FONTS = ['Source Serif 4', 'Inter', 'system-ui', 'Georgia', 'Arial'] as const;
export type SafeFont = (typeof SAFE_FONTS)[number];

/** Image-generation style presets woven into the visual-brief PROMPT TEXT only. */
export const STYLE_PRESETS = [
  'Photographic',
  'Editorial',
  'Minimal',
  'Bold graphic',
  'Warm lifestyle',
  'Studio product',
] as const;

/** Content type quick-start chips → the persisted content_type + default format. */
export const QUICK_STARTS: Array<{
  key: string;
  label: string;
  channel: Channel;
  format: 'single' | 'carousel';
  contentType: ContentType;
}> = [
  { key: 'ig-post', label: 'Instagram post', channel: 'instagram', format: 'single', contentType: 'post' },
  { key: 'ig-story', label: 'Instagram story', channel: 'instagram', format: 'single', contentType: 'post' },
  { key: 'fb-post', label: 'Facebook post', channel: 'facebook', format: 'single', contentType: 'post' },
  { key: 'li-post', label: 'LinkedIn post', channel: 'linkedin', format: 'single', contentType: 'post' },
  { key: 'carousel', label: 'Carousel', channel: 'instagram', format: 'carousel', contentType: 'post' },
];

export const MAX_CAROUSEL_PAGES = 10;

/**
 * HONEST status label (contract §1.H). Maps the two-axis machine to exactly one
 * label. 'Published' is only ever produced when a real provider_ref exists —
 * unreachable in the MVP, and never faked.
 */
export function statusLabel(
  review: ReviewStatus,
  publish: PublishState,
  scheduledFor: string | null,
  providerRef: string | null,
): { label: string; tone: 'neutral' | 'ai' | 'success' | 'warning' } {
  if (publish === 'published' && providerRef) return { label: 'Published', tone: 'success' };
  if (review === 'rejected') return { label: 'Changes requested', tone: 'warning' };
  if (review === 'draft') return { label: 'Draft', tone: 'neutral' };
  if (review === 'in_review') return { label: 'Awaiting approval', tone: 'ai' };
  // review === 'approved'
  if (scheduledFor && publish === 'connection_required') return { label: 'Scheduled — not auto-published', tone: 'warning' };
  if (scheduledFor) return { label: 'Scheduled locally', tone: 'ai' };
  return { label: 'Approved', tone: 'success' };
}
